/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
import React, { useEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { useConfirm } from 'material-ui-confirm'
import { ConfirmPopupRendererObject } from '@components/custom/confirmPopupRenderer/ConfirmPopupRenderer'
import ContraventionSnapshot from '../../components/ContraventionSnapshot'
import ReviewFooter from '../../components/ReviewFooter'

import './ReviewTab.css'
import VRMDetails from '../../components/VRMDetails'
import DiscardReasonsPopup from '../../popups/DiscardReasonsPopup'
import { ManualReviewAPIEndpoints } from '../../api'
import { fetchDataSelector } from '@recoil/selectors/selectors'
import { useTrackAnprLastActivity } from '@components/custom/anprLastActivity'
import {
  toastMessage,
  showLoading,
  selectedContractDetails,
  madeChanges,
} from '@recoil/atoms/atoms'
import utils from '@utils/utils'
import { useNavigate } from 'react-router-dom'
import { ROUTES, TASK_STATUS, MESSAGES } from '@pages/manualReviewTab/constants'

const ReviewTab = ({ observationDetails = {}, postDiscardRoute }) => {
  const [showDiscardPopup, setShowDiscardPopup] = useState(false)
  const [vehicleDetails, setVehicleDetails] = useState({
    vehicleMake: observationDetails?.vehicleDetails?.vehicleMake || '',
    vehicleColor: observationDetails?.vehicleDetails?.vehicleColor || '',
    vehicleType: observationDetails?.vehicleDetails?.vehicleTypeName || '',
  })
  const contractDetailsAtom = useRecoilValue(selectedContractDetails)
  const navigate = useNavigate()

  const getCoreData = useRecoilValue(fetchDataSelector)
  const setToastMessage = useSetRecoilState(toastMessage)
  const setShowLoading = useSetRecoilState(showLoading)
  const hasChanges = useRecoilValue(madeChanges)
  const confirmAlert = useConfirm()
  const { trackLastActivity } = useTrackAnprLastActivity({ autoTrack: false })

  useEffect(() => {
    setVehicleDetails({
      vehicleMake: observationDetails?.vehicleDetails?.vehicleMake || '',
      vehicleColor: observationDetails?.vehicleDetails?.vehicleColor || '',
      vehicleType: observationDetails?.vehicleDetails?.vehicleTypeName || '',
    })
  }, [observationDetails])

  const handleVehicleDetailsChange = (field, value) => {
    setVehicleDetails((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const isEmptySelection = (value) => {
    const normalizedValue = String(value || '').trim()
    return !normalizedValue || normalizedValue === '-- None --'
  }

  const isProceedDisabled =
    isEmptySelection(vehicleDetails?.vehicleMake) ||
    isEmptySelection(vehicleDetails?.vehicleColor) ||
    isEmptySelection(vehicleDetails?.vehicleType)

  const handleDiscard = async () => {
    if (hasChanges) {
      try {
        await confirmAlert({
          ...ConfirmPopupRendererObject(null, utils?.dataSaveWarnMessage),
        })
      } catch {
        return
      }
    }

    trackLastActivity()
    setShowDiscardPopup(true)
  }

  const handleCancelDiscard = () => {
    setShowDiscardPopup(false)
  }

  // Proceed handlers
  const handleProceed = async () => {
    if (hasChanges) {
      try {
        await confirmAlert({
          ...ConfirmPopupRendererObject(null, utils?.dataSaveWarnMessage),
        })
      } catch {
        return
      }
    }

    trackLastActivity()

    const proceedPayload = {
      vehicleMake: vehicleDetails?.vehicleMake || '',
      vehicleColor: vehicleDetails?.vehicleColor || '',
      vehicleType: vehicleDetails?.vehicleType || '',
    }

    setShowLoading(true)
    getCoreData(
      ManualReviewAPIEndpoints.saveProceed.method,
      ManualReviewAPIEndpoints.saveProceed.url
        ?.replace('__observationId__', observationDetails?.id)
        ?.replace('__contractId__', contractDetailsAtom?.contractId),
      proceedPayload,
      null,
      null,
      false,
      utils?.apiDomainName?.privateLand?.name
    )
      .then((response) => {
        if (
          response?.status === utils?.apiResponseCode?.successCode &&
          response?.data?.isCaseCreated
        ) {
          setToastMessage({
            message: response?.data?.message || MESSAGES?.proceed?.success,
            status: utils?.toastStatus?.success,
          })
          navigate(ROUTES?.MANUAL_REVIEW)
        } else {
          setToastMessage({
            message: response?.data?.message || MESSAGES?.proceed?.failed,
            status: utils?.toastStatus?.error,
          })
        }
      })
      .catch((error) => {
        setToastMessage({
          message: error?.response?.data?.message,
          status: utils?.toastStatus?.error,
        })
      })
      .finally(() => {
        setShowLoading(false)
      })
  }

  return (
    <div className="review-tab-container">
      <div className="main-content">
        <div className="contravention-snapshot-section">
          <ContraventionSnapshot observationDetails={observationDetails} />
        </div>
        <div className="case-details-wrapper">
          <div className="case-details-header">Case Details</div>
          <div className="vrm-details-section">
            <VRMDetails
              observationDetails={observationDetails}
              postDiscardRoute={postDiscardRoute}
              vehicleDetails={vehicleDetails}
              onVehicleDetailsChange={handleVehicleDetailsChange}
            />
          </div>
          {observationDetails?.taskStatus !== TASK_STATUS?.DISCARDED &&
            observationDetails?.taskStatus !== TASK_STATUS?.ISSUED && (
              <ReviewFooter
                onDiscard={handleDiscard}
                onProceed={handleProceed}
                disableProceed={isProceedDisabled}
              />
            )}
        </div>
      </div>
      {showDiscardPopup && (
        <DiscardReasonsPopup
          onCancel={handleCancelDiscard}
          onConfirm={() => {}}
          observationId={observationDetails?.id}
          contractId={contractDetailsAtom?.contractId}
          postDiscardRoute={postDiscardRoute}
        />
      )}
    </div>
  )
}

export default ReviewTab
